import { useMemo } from 'react';

import { skipToken } from '@apollo/client';
import { useSuspenseQuery } from 'data-fetching-client';
import { registerQuery } from 'data-fetching-client/next';
import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';

import {
  getUsersByIdsRpc,
  FullUserInformation,
  GetUsersByIdsRequest,
  GetUsersByIdsRequestType,
} from '../../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc';
import { useGetUserIdsOnHubByRoleIdAndPendingStatusSuspenseQuery } from '../../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc.hooks';
import {
  INTEGRATIONS_MANAGEMENT_ROLE_ID,
  SUPER_ADMIN_ROLE_ID,
} from '../../constants/roles';
import { chunk } from '../../utils/chunk';
import { useCaptureError } from '../useCaptureError';

const USER_IDS_BATCH_SIZE = 100;

const chirpClient = createRpcClientV2({ hubHttpClient: http });

const BATCHED_GET_USERS_BY_IDS_QUERY = registerQuery({
  fieldName: 'batched_getUsersByIds',
  args: ['userIds'],
  fetcher(request: GetUsersByIdsRequestType) {
    const userIds = request.userIds ?? [];

    if (userIds.length <= USER_IDS_BATCH_SIZE) {
      return chirpClient.call(getUsersByIdsRpc, { request });
    }

    const batches = chunk(userIds, USER_IDS_BATCH_SIZE);
    return Promise.all(
      batches.map((batchIds) =>
        chirpClient.call(getUsersByIdsRpc, {
          request: { userIds: batchIds },
        })
      )
    ).then((responses) => ({
      users: responses.flatMap((r) => r.users),
    }));
  },
});

const userIdsToUserMap = (users: FullUserInformation[]) => {
  return users.reduce<Record<number, FullUserInformation>>((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});
};

const useFetchUsers = (userIds?: GetUsersByIdsRequest['userIds']) => {
  const { error, ...rest } = useSuspenseQuery(BATCHED_GET_USERS_BY_IDS_QUERY, {
    variables: userIds === undefined ? skipToken : { userIds },
  });

  useCaptureError(error, {
    message: 'Error fetching users by ids',
  });

  return { error, ...rest };
};

const useFetchUsersByRoleId = (roleId: number) => {
  const { error, ...rest } =
    useGetUserIdsOnHubByRoleIdAndPendingStatusSuspenseQuery({
      variables: {
        roleId,
        includePending: false,
        includeInactive: false,
      },
    });

  useCaptureError(error, {
    message: 'Error fetching users by role id',
    extra: {
      roleId,
    },
  });

  return { error, ...rest };
};

function useFetchSuperAdminsUsers() {
  const { data } = useFetchUsersByRoleId(SUPER_ADMIN_ROLE_ID);
  return useFetchUsers(data?.userIds);
}

function useFetchIntegrationsManagementUsers() {
  const { data } = useFetchUsersByRoleId(INTEGRATIONS_MANAGEMENT_ROLE_ID);

  return useFetchUsers(data?.userIds);
}

export function useFetchUsersWithInstallAccess() {
  const { data: _superAdmins } = useFetchSuperAdminsUsers();
  const { data: _integrationsManagementWriteUsers } =
    useFetchIntegrationsManagementUsers();

  const superAdmins = useMemo(
    () => userIdsToUserMap(_superAdmins?.result.users),
    [_superAdmins]
  );

  const integrationsManagementWriteUsers = useMemo(
    () => userIdsToUserMap(_integrationsManagementWriteUsers?.result.users),
    [_integrationsManagementWriteUsers]
  );

  return {
    superAdmins,
    integrationsManagementWriteUsers,
  };
}
